import { 
  Search, 
  Heart,
  Award,
  Briefcase,
  GraduationCap,
  BookOpen,
  MessageCircle,
  Stethoscope,
  FileCheck,
  BarChart3,
  FileText,
  TrendingUp,
  Shield,
  Globe,
  Globe2,
  DollarSign,
  Brain,
  Zap,
  Coins,
  Network,
  Mail,
  Activity,
  Dna,
  Building2,
  Users,
  Settings,
  WifiOff,
  Target,
  Video,
  Newspaper,
  Cloud,
  ClipboardCheck,
  UserCheck,
  QrCode,
  // New icons for standalone landing pages
  ClipboardList,
  Camera,
  Siren,
  Database,
  FileQuestion,
  CircleDot,
  Microscope,
  Eye,
  Layers,
  Code,
  Lock,
  Wifi,
  FolderOpen,
  Landmark,
  Handshake,
  LifeBuoy
} from "lucide-react";
import { LucideIcon } from "lucide-react";
import { UserContext } from "./useUserContexts";

export interface NavigationItem {
  name: string;
  href: string;
  icon: LucideIcon;
  group?: string;
}

export interface NavigationStructure {
  [category: string]: NavigationItem[];
}

export interface SiteContextForNav {
  type: 'trial_site' | 'cro' | 'sponsor' | 'phc' | 'government' | 'training_cohort';
  id: string;
  display_name: string;
}

// Public marketing Solutions menu — shared by the signed-out nav AND the
// signed-in header (admins/staff previously lost the whole Solutions menu,
// including the Sponsor Journey, once logged in).
export const PUBLIC_SOLUTIONS_ITEMS: NavigationItem[] = [
  // By Role
  { name: "For Patients", href: "/patients", icon: Heart, group: "By Role" },
  { name: "For Medical Professionals", href: "/medical-professionals", icon: Stethoscope, group: "By Role" },
  { name: "For Trial Sites", href: "/trial-sites", icon: Briefcase, group: "By Role" },
  { name: "For Sponsors", href: "/sponsors", icon: DollarSign, group: "By Role" },
  { name: "Free 72-Hour Feasibility", href: "/for-sponsors/feasibility", icon: Target, group: "By Role" },
  { name: "PI: Get Feasibility-Ready", href: "/for-sites/feasibility-ready", icon: Target, group: "By Role" },
  { name: "CRO Services", href: "/cro-services", icon: Building2, group: "By Role" },
  { name: "SMO Services", href: "/smo-services", icon: Users, group: "By Role" },
  // Sponsor Journey (investment ladder: L0 identify -> L3 scoping sprint)
  { name: "Sponsor Hub", href: "/for-sponsors", icon: Briefcase, group: "Sponsor Journey" },
  { name: "Free 72-Hour Feasibility", href: "/for-sponsors/feasibility", icon: Target, group: "Sponsor Journey" },
  { name: "Architecture & Sovereignty", href: "/for-sponsors/architecture", icon: Layers, group: "Sponsor Journey" },
  { name: "The Verified File", href: "/for-sponsors/verified-file", icon: FileCheck, group: "Sponsor Journey" },
  { name: "Evidence & Site Readiness", href: "/for-sponsors/evidence", icon: ClipboardCheck, group: "Sponsor Journey" },
  { name: "Investigator Voice", href: "/for-sponsors/investigator-voice", icon: Users, group: "Sponsor Journey" },
  { name: "Scoping Sprint", href: "/for-sponsors/scoping-sprint", icon: Target, group: "Sponsor Journey" },
  { name: "Talk to the Sponsor Desk", href: "/for-sponsors/contact", icon: Mail, group: "Sponsor Journey" },
  // Products
  { name: "BRIDGE - Africa Digital Layer", href: "/bridge", icon: Network, group: "Products" },
  { name: "eCOA Suite", href: "/ecoa", icon: ClipboardList, group: "Products" },
  { name: "Primary Health Care (PHC)", href: "/solutions/phc", icon: Stethoscope, group: "Products" },
  { name: "Veeva Vault eTMF", href: "/veeva-etmf", icon: Database, group: "Products" },
  { name: "Emergency SOS", href: "/emergency-sos", icon: Siren, group: "Products" },
  { name: "Inspection Readiness", href: "/inspection-readiness", icon: ClipboardCheck, group: "Products" },
  // Pricing & Services
  { name: "Services", href: "/services", icon: FileCheck, group: "Pricing & Services" },
  { name: "Features", href: "/features", icon: Award, group: "Pricing & Services" },
  { name: "Trial Site Pricing", href: "/pricing/trial-sites", icon: DollarSign, group: "Pricing & Services" },
  // Regulatory
  { name: "Trust & Evidence Hub", href: "/trust", icon: Shield, group: "Regulatory" },
  { name: "EFDA SaMD Compliance (Ethiopia)", href: "/resources/efda-samd-compliance", icon: FileCheck, group: "Regulatory" },
  { name: "TMDA SaMD Compliance (Tanzania)", href: "/resources/tmda-samd-compliance", icon: FileCheck, group: "Regulatory" },
  { name: "NDA SaMD Compliance (Uganda)", href: "/resources/nda-samd-compliance", icon: FileCheck, group: "Regulatory" },
  { name: "PPB SaMD Compliance (Kenya)", href: "/resources/ppb-samd-compliance", icon: FileCheck, group: "Regulatory" },
  { name: "NAFDAC SaMD Compliance (Nigeria)", href: "/resources/nafdac-samd-compliance", icon: FileCheck, group: "Regulatory" },
  { name: "FDA Ghana SaMD Compliance (Ghana)", href: "/resources/fda-ghana-samd-compliance", icon: FileCheck, group: "Regulatory" },
  { name: "ANARME SaMD Compliance (Mozambique)", href: "/resources/anarme-samd-compliance", icon: FileCheck, group: "Regulatory" },
  { name: "PMRA SaMD Compliance (Malawi)", href: "/resources/pmra-samd-compliance", icon: FileCheck, group: "Regulatory" },
  { name: "SAHPRA SaMD Compliance (South Africa)", href: "/resources/sahpra-samd-compliance", icon: FileCheck, group: "Regulatory" },
  // UmojaReg — PTC BRIDGE authority surface (EDCTP3 SERP-01). Hosts WHO GBT, TwinNet, AVAREF, regulator ToT.
  { name: "UmojaReg (BRIDGE Authority)", href: "/umojareg", icon: Landmark, group: "Regulatory" },
  // The links below now 301 to UmojaReg (see src/routes/umojareg.redirects.ts)
  { name: "EthicsTrack & ToT LMS", href: "/ethics-track", icon: Shield, group: "Regulatory" },
  // TwinNet is an NRA-owned capability inside each regulator's UmojaReg tenant — never a BRIDGE module.
  { name: "TwinNet™ (NRA-owned, on UmojaReg)", href: "/twinnet", icon: Shield, group: "Regulatory" },
  { name: "WHO GBT Dashboard", href: "https://umojareg.org/umoja/gbt/dashboard", icon: Globe, group: "Regulatory" },
  // Product Explainers
  { name: "BRIDGE Suite Overview", href: "/solutions/bridge-suite", icon: Network, group: "Product Explainers" },
  { name: "What is SiteLink?", href: "/solutions/sitelink", icon: Eye, group: "Product Explainers" },
  { name: "What is SiteCommand?", href: "/solutions/sitecommand", icon: Building2, group: "Product Explainers" },
  { name: "What is CDMS?", href: "/solutions/cdms", icon: Database, group: "Product Explainers" },
  // CTMS Pillar (SEO outrank cluster)
  { name: "Clinical Trial Management Software (CTMS)", href: "/ctms", icon: Database, group: "CTMS" },
  { name: "CTMS Investigator Credentials", href: "/ctms/credentials", icon: FileCheck, group: "CTMS" },
  { name: "CTMS for Africa", href: "/ctms/africa", icon: Globe, group: "CTMS" },
  { name: "CTMS Comparisons", href: "/vs", icon: FileCheck, group: "CTMS" },
  { name: "PTC vs Medidata", href: "/vs/medidata", icon: FileCheck, group: "CTMS" },
  { name: "PTC vs Veeva", href: "/vs/veeva", icon: FileCheck, group: "CTMS" },
  { name: "PTC vs Oracle", href: "/vs/oracle", icon: FileCheck, group: "CTMS" },
  { name: "PTC vs Florence", href: "/vs/florence", icon: FileCheck, group: "CTMS" },
];

export function useNavigationItems(
  role: string | null | undefined, 
  isAdminFlag?: boolean, 
  context?: UserContext | null,
  contexts?: UserContext[],
  siteContext?: SiteContextForNav | null
) {
  const normalizedRole = (role ?? '').toLowerCase();
  const isAdminRole = !!isAdminFlag || normalizedRole === 'admin' || normalizedRole === 'super_admin';
  
  // PRIORITY 1: If on SMO/CRO white-label subdomain, ALWAYS return SMO/CRO navigation
  // This overrides admin role on SMO/CRO subdomains
  // SMO/CRO menu excludes: Data Monetization (Admin Only), Quantum Predictor (Admin Only)
  // Trial Analytics/Management link to SMO/CRO-specific pages for their organization's trials
  if (siteContext?.type === 'cro') {
    return {
      smoCroOperations: [
        { name: "Command Center", href: "/cro/command-center", icon: BarChart3 },
        { name: "Site Portfolio", href: "/cro/command-center?tab=sites", icon: Building2 },
        { name: "Site CRM", href: "/cro/command-center?tab=crm", icon: Users },
        { name: "Staff", href: "/cro/command-center?tab=staff", icon: UserCheck },
        { name: "Sponsors", href: "/cro/command-center?tab=sponsors", icon: Briefcase },
        { name: "Monitoring", href: "/cro/command-center?tab=monitoring", icon: Activity },
        { name: "RFP Management", href: "/cro/command-center?tab=rfp", icon: FileText },
        { name: "Email Sites", href: "/cro/command-center?tab=email", icon: Mail },
        { name: "Financials", href: "/cro/command-center?tab=financials", icon: DollarSign },
        { name: "eTMF & Inspection", href: "/cro/command-center?tab=etmf", icon: FileCheck },
        { name: "Integrations", href: "/cro/command-center?tab=integrations", icon: Network },
      ],
      analytics: [
        { name: "Analytics", href: "/cro/command-center?tab=analytics", icon: TrendingUp },
        { name: "Reports", href: "/cro/command-center?tab=reports", icon: BarChart3 },
        { name: "Compliance", href: "/cro/command-center?tab=compliance", icon: Shield },
        { name: "eCOA", href: "/cro/command-center?tab=ecoa", icon: ClipboardList },
        { name: "Testing", href: "/cro/command-center?tab=testing", icon: Zap },
      ],
      academy: [
        { name: "BRIDGE Academy", href: "/academy", icon: GraduationCap },
      ],
      resources: [
        { name: "Documentation", href: "/cro/command-center?tab=docs", icon: BookOpen },
        { name: "SOPs", href: "/sops", icon: FileText },
        { name: "SMO/CRO Assessment", href: "/cro-assessment", icon: FileCheck },
        { name: "Training Videos", href: "/trial-site/training-videos", icon: Video },
        { name: "Integration Hub", href: "/dashboard/integration-hub", icon: Network },
        { name: "Settings", href: "/cro/command-center?tab=settings", icon: Settings },
      ],
    };
  }

  // PRIORITY 2: If on SPONSOR white-label subdomain, ALWAYS return sponsor navigation
  // This overrides admin role on sponsor subdomains - admin users see sponsor menu, not admin menu
  if (siteContext?.type === 'sponsor') {
    return {
      trialManagement: [
        { name: "Command Center", href: "/sponsor/command-center", icon: BarChart3 },
        { name: "Patient Requests", href: "/sponsor-patient-requests", icon: Users },
        { name: "Site Search", href: "/sponsor-site-search", icon: Search },
        { name: "Trial Analytics", href: "/sponsor-analytics", icon: TrendingUp },
      ],
      businessTools: [
        { name: "Success Metrics", href: "/sponsor-success", icon: Award },
        { name: "Patient Referrals", href: "/referrals/review", icon: Target },
        { name: "Integration Hub", href: "/dashboard/integration-hub", icon: Network },
        { name: "Financials", href: "/sponsor-financials", icon: DollarSign },
      ],
      academy: [
        { name: "Education", href: "/education", icon: GraduationCap },
        { name: "BRIDGE Academy", href: "/academy", icon: GraduationCap },
      ],
      resources: [
        { name: "SOPs", href: "/sops", icon: BookOpen },
        { name: "Training Videos", href: "/trial-site/training-videos", icon: Video },
      ],
    };
  }

  // PRIORITY 3: If on TRIAL SITE white-label subdomain, ALWAYS return trial site navigation
  // This overrides admin role on trial site subdomains - admin users see trial site menu, not admin menu
  if (siteContext?.type === 'trial_site') {
    return {
      siteOperations: [
        { name: "Dashboard", href: `/trial-site/${siteContext.id}`, icon: BarChart3 },
        { name: "eCRF", href: `/trial-site/${siteContext.id}/ecrf`, icon: FileCheck },
        { name: "Analytics", href: `/trial-site/${siteContext.id}?tab=analytics`, icon: TrendingUp },
        { name: "Staff", href: `/trial-site/${siteContext.id}?tab=staff`, icon: Users },
        { name: "Patients", href: `/trial-site/${siteContext.id}?tab=patients`, icon: Users },
        { name: "CRM", href: `/trial-site/${siteContext.id}?tab=crm`, icon: Briefcase },
        { name: "eConsent", href: `/trial-site/${siteContext.id}?tab=econsent`, icon: FileCheck },
        { name: "IP Management", href: `/trial-site/${siteContext.id}?tab=ip-management`, icon: Shield },
        { name: "Queries", href: `/trial-site/${siteContext.id}?tab=queries`, icon: MessageCircle },
        { name: "Regulatory", href: `/trial-site/${siteContext.id}?tab=regulatory`, icon: FileText },
        { name: "EthicsTrack", href: `/trial-site/${siteContext.id}?tab=ethics`, icon: Shield },
        { name: "ToT LMS", href: `/trial-site/${siteContext.id}?tab=tot-lms`, icon: GraduationCap },
        { name: "Settings", href: `/trial-site/${siteContext.id}?tab=settings`, icon: Settings },
      ],
      referralReview: [
        { name: "Referral Review", href: "/referrals/review", icon: Target },
      ],
      academy: [
        { name: "BRIDGE Academy", href: "/academy", icon: GraduationCap },
      ],
      resources: [
        { name: "Training Videos", href: "/trial-site/training-videos", icon: Video },
        { name: "Help Centre", href: "/help-center", icon: LifeBuoy },
        { name: "CRA Monitoring One-Pager", href: "/resources/cra-one-pager", icon: Eye },
        { name: "Quick Training", href: "/phc/print/bridge-trial-site-quick-training", icon: GraduationCap },
        { name: "Site Assessment (paper form)", href: `/site-assessment/print?siteId=${siteContext.id}`, icon: FileText },
        { name: "Trial Site Manual", href: "/phc/print/bridge-trial-site-user-manual", icon: BookOpen },
        { name: "SOPs (CT-01..CT-16)", href: "/phc/print/bridge-trial-site-sops", icon: FileText },
        { name: "Train the Trainer", href: "/phc/print/bridge-trial-site-train-the-trainer", icon: GraduationCap },
        { name: "SOP Library", href: "/sops", icon: FileText },
        { name: "Integration Hub", href: "/dashboard/integration-hub", icon: Network },
        { name: "DCT Infrastructure", href: "/decentralized-trials", icon: Network },
      ],
    };
  }
  
  // Use actual role, but filter menu items by context for multi-context users
  const effectiveRole = normalizedRole;
  const isMultiContext = contexts && contexts.length > 1;
  const shouldFilterByContext = isMultiContext && context;

  // Public navigation (non-authenticated users)
  if (!role && !isAdminRole) {
    return {
      platform: [
        { name: "Browse Trials", href: "/trials", icon: Search },
        { name: "Platform Roadmap", href: "/roadmap", icon: TrendingUp },
        { name: "Directory", href: "/directory", icon: Users },
      ],
      conditions: [
        { name: "All Conditions", href: "/conditions", icon: Heart },
        { name: "Cancer Trials", href: "/conditions/cancer", icon: Heart, subcategories: [
          { name: "Breast Cancer", href: "/conditions/cancer/breast-cancer" },
          { name: "Lung Cancer", href: "/conditions/cancer/lung-cancer" },
          { name: "Colorectal Cancer", href: "/conditions/cancer/colorectal" },
          { name: "Prostate Cancer", href: "/conditions/cancer/prostate-cancer" },
          { name: "Melanoma", href: "/conditions/cancer/melanoma" },
          { name: "Leukemia & Lymphoma", href: "/conditions/cancer/leukemia-lymphoma" },
        ]},
        { name: "Diabetes Trials", href: "/conditions/diabetes", icon: Activity, subcategories: [
          { name: "Type 1 Diabetes", href: "/conditions/diabetes/type1-diabetes" },
          { name: "Type 2 Diabetes", href: "/conditions/diabetes/type2-diabetes" },
          { name: "Gestational Diabetes", href: "/conditions/diabetes/gestational-diabetes" },
        ]},
        { name: "Heart Disease", href: "/conditions/heart-disease", icon: Heart, subcategories: [
          { name: "Coronary Artery Disease", href: "/conditions/heart/coronary-artery-disease" },
          { name: "Heart Failure", href: "/conditions/heart/heart-failure" },
          { name: "Arrhythmia", href: "/conditions/heart/arrhythmia" },
          { name: "Hypertension", href: "/conditions/heart/hypertension" },
        ]},
        { name: "Alzheimer's & Dementia", href: "/conditions/alzheimers", icon: Brain, subcategories: [
          { name: "Alzheimer's Disease", href: "/conditions/alzheimers/alzheimers-disease" },
          { name: "Vascular Dementia", href: "/conditions/alzheimers/vascular-dementia" },
          { name: "Mild Cognitive Impairment", href: "/conditions/alzheimers/mci" },
        ]},
        { name: "Rare Diseases", href: "/conditions/rare-diseases", icon: Dna },
        { name: "Rheumatoid Arthritis", href: "/conditions/rheumatoid-arthritis", icon: Stethoscope, subcategories: [
          { name: "Psoriatic Arthritis", href: "/conditions/arthritis/psoriatic-arthritis" },
          { name: "Osteoarthritis", href: "/conditions/arthritis/osteoarthritis" },
        ]},
      ],
      technology: [
        { name: "AI Clinical Decision Support", href: "/solutions/ai-cdst", icon: Brain },
        { name: "Medical Imaging AI", href: "/medical-imaging", icon: Camera },
        { name: "RFI Auto-Response", href: "/rfi-response", icon: FileQuestion },
        { name: "Integration Hub", href: "/integration-hub", icon: Zap },
        { name: "Decentralized Trials", href: "/decentralized-trials", icon: Network },
        { name: "Offline-First Technology", href: "/offline-first", icon: WifiOff },
        { name: "Patient Rewards", href: "/token-economy", icon: Coins },
      ],
      solutions: PUBLIC_SOLUTIONS_ITEMS,
      academy: [
        { name: "Education", href: "/education", icon: GraduationCap, group: "Academy" },
        { name: "PTC Academy", href: "/ptc-academy", icon: Award, group: "Academy" },
        { name: "PHC Academy", href: "/phc-academy", icon: Award, group: "Academy" },
        { name: "TwinNet Academy", href: "/twinnet-academy", icon: Award, group: "Academy" },
      ],
      resources: [
        // Trust & Compliance
        { name: "Data Residency & Hosting", href: "/trust/data-residency", icon: Globe2, group: "Trust & Compliance" },
        { name: "Trust Center", href: "/trust", icon: Shield, group: "Trust & Compliance" },
        // Guides & Education
        { name: "Webinars", href: "/resources/webinars", icon: Video, group: "Guides & Education" },
        { name: "Blog", href: "/blog", icon: FileText, group: "Guides & Education" },
        { name: "News", href: "/news", icon: Newspaper, group: "Guides & Education" },
        { name: "Guides", href: "/guides", icon: BookOpen, group: "Guides & Education" },
        { name: "Platform Guides", href: "/resources/platform-guides", icon: BookOpen, group: "Guides & Education" },
        { name: "FDA Inspection Guide", href: "/resources/fda-inspection-guide", icon: Shield, group: "Guides & Education" },
        { name: "eCOA User Manual", href: "/ecoa-manual", icon: BookOpen, group: "Guides & Education" },
        // One-Pagers & Collateral
        { name: "Bridge One-Pager", href: "/resources/bridge-one-pager", icon: FileText, group: "One-Pagers" },
        { name: "BRIDGE 6 Levels of Sovereignty", href: "/resources/bridge-six-levels-of-sovereignty", icon: Layers, group: "One-Pagers" },
        { name: "BRIDGE Suite One-Pager", href: "/resources/bridge-suite-one-pager", icon: Layers, group: "One-Pagers" },
        { name: "SiteLink™ One-Pager", href: "/resources/sitelink-one-pager", icon: Eye, group: "One-Pagers" },
        { name: "Directory — For Trial Sites", href: "/resources/directory-for-sites", icon: Building2, group: "One-Pagers" },
        { name: "Directory — For Sponsors", href: "/resources/directory-for-sponsors", icon: Users, group: "One-Pagers" },
        { name: "CRO One-Pager", href: "/resources/cro-one-pager", icon: FileText, group: "One-Pagers" },
        { name: "PHC One-Pager", href: "/resources/phc-one-pager", icon: Stethoscope, group: "One-Pagers" },
        { name: "VHF Outbreak Intelligence", href: "/resources/vhf-outbreak-intelligence-one-pager", icon: Activity, group: "One-Pagers" },
        { name: "eTMF One-Pager", href: "/resources/etmf-one-pager", icon: FolderOpen, group: "One-Pagers" },
        { name: "CDMS One-Pager", href: "/resources/cdms-one-pager", icon: Database, group: "One-Pagers" },
        { name: "Billing One-Pager", href: "/resources/billing-one-pager", icon: DollarSign, group: "One-Pagers" },
        { name: "CTMS One-Pager", href: "/resources/ctms-one-pager", icon: ClipboardList, group: "One-Pagers" },
        { name: "CRA Monitoring One-Pager", href: "/resources/cra-one-pager", icon: Eye, group: "One-Pagers" },
        { name: "Africa PHC Uplift", href: "/resources/africa-phc-uplift", icon: Globe2, group: "One-Pagers" },
        { name: "Africa Digitization", href: "/resources/africa-digitization-one-pager", icon: Globe, group: "One-Pagers" },
        { name: "Sponsor Architecture", href: "/for-sponsors/architecture", icon: Globe, group: "One-Pagers" },
        { name: "Gov Surveillance", href: "/resources/gov-surveillance-one-pager", icon: Landmark, group: "One-Pagers" },
        { name: "eCTD & NRA Submission", href: "/resources/ectd-one-pager", icon: FileText, group: "One-Pagers" },
        { name: "AMR & ID Protocols", href: "/resources/edctp3-id-protocols", icon: Activity, group: "One-Pagers" },
        { name: "Patient One-Pager", href: "/resources/patient-one-pager", icon: Heart, group: "One-Pagers" },
        { name: "Patient Self-Check (AI-CDST)", href: "/resources/patient-self-check-cdst-one-pager", icon: Stethoscope, group: "One-Pagers" },
        { name: "Pipeline One-Pager", href: "/resources/pipeline-one-pager", icon: TrendingUp, group: "One-Pagers" },
        { name: "AMR Africa", href: "/resources/amr-africa", icon: Microscope, group: "One-Pagers" },
        { name: "Sponsor Pitch Deck", href: "/resources/sponsor-pitch", icon: DollarSign, group: "One-Pagers" },
        { name: "EDCTP3 Telehealth", href: "/resources/edctp3-telehealth", icon: Wifi, group: "One-Pagers" },
        { name: "UGHE Clinical Trials", href: "/resources/ughe-clinical-trials", icon: GraduationCap, group: "One-Pagers" },
        { name: "EthicsTrack™", href: "/resources/ethics-track-one-pager", icon: Shield, group: "One-Pagers" },
        { name: "Site Readiness One-Pager", href: "/resources/site-readiness-one-pager", icon: ClipboardCheck, group: "One-Pagers" },
        { name: "Oncology Site Readiness", href: "/resources/oncology-site-readiness-one-pager", icon: ClipboardCheck, group: "One-Pagers" },
        { name: "ToT LMS", href: "/resources/tot-lms-one-pager", icon: GraduationCap, group: "One-Pagers" },
        { name: "Regulatory & Ethics Fragmentation", href: "/resources/regulatory-fragmentation-one-pager", icon: Landmark, group: "One-Pagers" },
        { name: "UmojaReg One-Pager", href: "/resources/umojareg-one-pager", icon: Landmark, group: "One-Pagers" },
        { name: "African AI for Health", href: "/resources/african-ai-for-health", icon: Brain, group: "One-Pagers" },
        // Case Studies
        { name: "UCTH Case Study", href: "/resources/ucth-case-study", icon: Building2, group: "Case Studies" },
        { name: "OPTIMISE-TB Case Study", href: "/resources/optimise-tb-case-study", icon: Building2, group: "Case Studies" },
        { name: "Serurubele REDCap Case Study", href: "/resources/serurubele-redcap-case-study", icon: Building2, group: "Case Studies" },
        { name: "EVAH Case Study", href: "/evah", icon: Globe, group: "Case Studies" },
        { name: "AERO-VASC Study", href: "/resources/aero-vasc-study", icon: Heart, group: "Case Studies" },
        { name: "Success Stories", href: "/sponsor-success", icon: Award, group: "Case Studies" },
        { name: "UCTH Blockchain Report", href: "/resources/ucth-blockchain-report", icon: FileCheck, group: "Case Studies" },
        { name: "AERO-VASC Protocol", href: "/resources/aero-vasc-protocol", icon: FileText, group: "Case Studies" },
        { name: "AERO-VASC Corridor", href: "/resources/aero-vasc-corridor", icon: Globe, group: "Case Studies" },
        // Product Explainers (now under Solutions)
        { name: "BRIDGE Suite Overview", href: "/solutions/bridge-suite", icon: Network, group: "Product Explainers" },
        { name: "What is SiteLink?", href: "/solutions/sitelink", icon: Eye, group: "Product Explainers" },
        { name: "What is SiteCommand?", href: "/solutions/sitecommand", icon: Building2, group: "Product Explainers" },
        { name: "What is CDMS?", href: "/solutions/cdms", icon: Database, group: "Product Explainers" },
        { name: "BlueCloudX", href: "/bluecloudx", icon: Cloud, group: "Product Explainers" },
        { name: "BRIDGE + CRIO", href: "/resources/bridge-crio-one-pager", icon: Network, group: "Product Explainers" },
        { name: "Dental Trial Sites", href: "/dental-trial-sites", icon: CircleDot, group: "Product Explainers" },
        { name: "Medical Equipment", href: "/medical-equipment-trials", icon: Microscope, group: "Product Explainers" },
        // Compliance & Developer
        { name: "Security Hub", href: "/resources/security-hub", icon: Lock, group: "Compliance & Developer" },
        { name: "API Documentation", href: "/resources/api-documentation", icon: Code, group: "Compliance & Developer" },
        { name: "Health System Integrations", href: "/resources/health-system-integrations", icon: Wifi, group: "Compliance & Developer" },
        { name: "Validation Documentation", href: "/resources/validation-documentation", icon: ClipboardCheck, group: "Compliance & Developer" },
        { name: "FDORA Readiness Toolkit", href: "/resources/fdora-toolkit", icon: ClipboardList, group: "Compliance & Developer" },
        { name: "FDORA Calculator", href: "/resources/fdora-calculator", icon: ClipboardList, group: "Compliance & Developer" },
        { name: "CTMS ROI Calculator", href: "/resources/ctms-roi-calculator", icon: ClipboardList, group: "Compliance & Developer" },
        { name: "Editorial Team", href: "/about/editorial-team", icon: Award, group: "Compliance & Developer" },
        { name: "Competitive Landscape", href: "/resources/competitive-landscape", icon: Target, group: "Compliance & Developer" },
        { name: "Competitive Analysis", href: "/resources/competitive-analysis", icon: TrendingUp, group: "Compliance & Developer" },
        { name: "BRIDGE vs Florence", href: "/resources/bridge-florence-comparison", icon: FileText, group: "Compliance & Developer" },
        { name: "Security Architecture", href: "/resources/security-architecture", icon: Shield, group: "Compliance & Developer" },
        { name: "Incident Response Plan", href: "/resources/incident-response-plan", icon: Shield, group: "Compliance & Developer" },
        { name: "PDPL Compliance", href: "/resources/pdpl-compliance", icon: FileCheck, group: "Compliance & Developer" },
        { name: "Security Compliance", href: "/resources/security-compliance", icon: Lock, group: "Compliance & Developer" },
        { name: "VAPT Readiness", href: "/resources/vapt-readiness", icon: ClipboardCheck, group: "Compliance & Developer" },
        { name: "Change Management Log", href: "/resources/change-management-log", icon: FileText, group: "Compliance & Developer" },
        // Directories & Tools
        { name: "Directory", href: "/directory", icon: Users, group: "Directories & Tools" },
        { name: "Research Centers", href: "/directory/research-centers", icon: Building2, group: "Directories & Tools" },
        { name: "Advocacy Groups", href: "/directory/advocacy", icon: Heart, group: "Directories & Tools" },
        { name: "Assessments", href: "/assessments", icon: Award, group: "Directories & Tools" },
        { name: "Free Site Assessment", href: "/free-site-assessment", icon: ClipboardCheck, group: "Directories & Tools" },
        { name: "PI: Get Feasibility-Ready", href: "/for-sites/feasibility-ready", icon: ClipboardCheck, group: "Directories & Tools" },
        { name: "QR Codes", href: "/resources/qr-codes", icon: QrCode, group: "Directories & Tools" },
        { name: "Roadmap", href: "/roadmap", icon: TrendingUp, group: "Directories & Tools" },
        { name: "PHC Readiness Assessment", href: "/resources/phc-readiness", icon: Stethoscope, group: "Directories & Tools" },
        // WHO GBT Dashboard removed from top nav — still reachable via /resources
        { name: "TCIS Conference", href: "/resources/tcis-conference", icon: Users, group: "Directories & Tools" },
        // Partners
        { name: "GulaPro Limited", href: "/resources/partners/gulapro", icon: Handshake, group: "Partners" },
        { name: "Partnerships", href: "/partnerships", icon: Handshake, group: "Partners" },
      ],
      company: [
        { name: "About", href: "/about", icon: Users },
        { name: "Mission", href: "/mission", icon: Heart },
        { name: "Careers", href: "/careers", icon: Briefcase },
        { name: "Contact", href: "/contact", icon: MessageCircle },
      ]
    };
  }

  // Admin users get the admin navigation in the main menu
  if (isAdminRole) {
    return {
      operations: [
        { name: "Command Center", href: "/admin/command-center", icon: BarChart3 },
        { name: "Patient Referrals", href: "/referrals/review", icon: Users },
        
        { name: "Directory Listings", href: "/admin/directory-listings", icon: Building2 },
        { name: "Revenue", href: "/admin/revenue", icon: DollarSign },
        { name: "AMR Surveillance", href: "/admin/amr-surveillance", icon: Microscope },
        { name: "PHC Mode", href: "/phc", icon: Stethoscope },
      ],
      academy: [
        { name: "BRIDGE Academy", href: "/admin/academy", icon: GraduationCap },
      ],
      content: [
        { name: "Blog Posts", href: "/admin/blog-posts", icon: FileText },
        { name: "News Management", href: "/admin/news", icon: FileText },
        { name: "Webinars", href: "/admin/webinars", icon: Video },
        { name: "Advocacy Partners", href: "/admin/advocacy", icon: Users },
      ],
      trialRegistries: [
        // Consolidated - all registries accessible from Registry Hub
        { name: "Registry Hub", href: "/admin/registry-hub", icon: Globe },
        { name: "Research Center Import", href: "/admin/research-centers/import", icon: Building2 },
      ],
      system: [
        { name: "Notifications", href: "/admin/notifications", icon: Mail },
        { name: "Permissions", href: "/admin/permissions", icon: Shield },
        { name: "Logs", href: "/admin/logs", icon: FileText },
        { name: "Settings", href: "/admin/settings", icon: Settings },
        { name: "Sitemap", href: "/admin/sitemap", icon: Globe },
      ],
      resources: []

    };
  }

  // Helper function to filter routes by context
  const isRouteAllowedInContext = (href: string, currentContext: UserContext): boolean => {
    if (!shouldFilterByContext) return true; // No filtering if single context
    
    const professionalRoutes = [
      '/referrals/intake', '/professional-analytics', '/ai-enhancements',
      '/behavioral-analyzer', '/dashboard/integration-hub'
    ];
    
    const siteRoutes = [
      '/site-dashboard', '/site-assessment', '/referrals/review',
      '/behavioral-analyzer', '/dashboard/integration-hub'
    ];
    
    const sharedRoutes = [
      '/referrals/review', '/education', '/roadmap', '/sops', 
      '/community-dashboard', '/directory', '/profile', '/settings'
    ];
    
    // Always allow shared routes
    if (sharedRoutes.some(route => href.startsWith(route))) {
      return true;
    }
    
    // Context-specific filtering
    if (currentContext === 'professional') {
      return professionalRoutes.some(route => href.startsWith(route));
    } else if (currentContext === 'site') {
      return siteRoutes.some(route => href.startsWith(route));
    }
    
    return true; // Default: allow
  };

  const filterNavigationByContext = (nav: NavigationStructure): NavigationStructure => {
    if (!shouldFilterByContext || !context) return nav;
    
    const filtered: NavigationStructure = {};
    
    for (const [category, items] of Object.entries(nav)) {
      const filteredItems = items.filter(item => 
        isRouteAllowedInContext(item.href, context)
      );
      
      if (filteredItems.length > 0) {
        filtered[category] = filteredItems;
      }
    }
    
    return filtered;
  };

  // Authenticated user navigation (role-based with context filtering)
  const getBaseNavigation = (): NavigationStructure => {
    switch (effectiveRole) {
    case 'patient_caregiver':
      return {
        myTrials: [
          { name: "Browse Trials", href: "/trials", icon: Search },
          { name: "My Matches", href: "/matches", icon: Heart },
          { name: "Progress", href: "/progress", icon: Award },
        ],
        dashboard: [
          { name: "My Dashboard", href: "/patient-dashboard", icon: BarChart3 },
          { name: "Health Passport", href: "/health-passport", icon: Shield },
          { name: "Token Dashboard", href: "/dashboard/token-economy", icon: Coins },
        ],
        resources: [
          { name: "Education", href: "/education", icon: GraduationCap },
          { name: "Webinars", href: "/resources/webinars", icon: Video },
          { name: "Roadmap", href: "/roadmap", icon: TrendingUp },
          { name: "SOPs", href: "/sops", icon: BookOpen },
          { name: "Community", href: "/community-dashboard", icon: MessageCircle },
        ]
      };

    case 'medical_professional':
    case 'healthcare_provider':
      return {
        patients: [
          { name: "Referral Intake", href: "/referrals/intake", icon: Stethoscope },
          { name: "Referral Review", href: "/referrals/review", icon: Users },
          { name: "Directory", href: "/directory", icon: Users },
        ],
        professionalTools: [
          { name: "Analytics", href: "/professional-analytics", icon: BarChart3 },
          { name: "AI Enhancements", href: "/ai-enhancements", icon: Award },
          { name: "Behavioral Analyzer", href: "/behavioral-analyzer", icon: Zap },
          { name: "Integration Hub", href: "/dashboard/integration-hub", icon: Network },
        ],
        academy: [
          { name: "Education", href: "/education", icon: GraduationCap },
          { name: "BRIDGE Academy", href: "/academy", icon: GraduationCap },
        ],
        resources: [
          { name: "Webinars", href: "/resources/webinars", icon: Video },
          { name: "Roadmap", href: "/roadmap", icon: TrendingUp },
          { name: "SOPs", href: "/sops", icon: BookOpen },
          { name: "Community", href: "/community-dashboard", icon: MessageCircle },
        ]
      };

    case 'sponsor_service_provider':
      return {
        trialManagement: [
          { name: "Command Center", href: "/sponsor/command-center", icon: BarChart3 },
          { name: "Patient Requests", href: "/sponsor-patient-requests", icon: Users },
          { name: "Site Search", href: "/sponsor-site-search", icon: Search },
          { name: "Trial Analytics", href: "/sponsor-analytics", icon: BarChart3 },
        ],
        businessTools: [
          { name: "Pricing", href: "/pricing", icon: DollarSign },
          { name: "Success Metrics", href: "/sponsor-success", icon: Award },
          { name: "Patient Referrals", href: "/referrals/review", icon: Users },
          { name: "Behavioral Analyzer", href: "/behavioral-analyzer", icon: Zap },
          { name: "Integration Hub", href: "/dashboard/integration-hub", icon: Network },
          { name: "Token Economy", href: "/dashboard/token-economy", icon: Coins },
        ],
        academy: [
          { name: "Education", href: "/education", icon: GraduationCap },
          { name: "BRIDGE Academy", href: "/academy", icon: GraduationCap },
        ],
        resources: [
          { name: "Webinars", href: "/resources/webinars", icon: Video },
          { name: "Roadmap", href: "/roadmap", icon: TrendingUp },
          { name: "SOPs", href: "/sops", icon: BookOpen },
        ],
      };

    // TRIAL SITE navigation - follows WHITE_LABEL_NAVIGATION_STANDARD.md exactly
    case 'trial_site':
      return {
        siteOperations: [
          { name: "Dashboard", href: "/site-dashboard", icon: BarChart3 },
          { name: "eCRF", href: "/site-ecrf", icon: FileCheck },
          { name: "Analytics", href: "/site-analytics", icon: TrendingUp },
          { name: "Staff", href: "/site-staff", icon: Users },
          { name: "Patients", href: "/site-patients", icon: Users },
          { name: "CRM", href: "/site-crm", icon: Briefcase },
          { name: "eConsent", href: "/site-econsent", icon: FileCheck },
          { name: "IP Management", href: "/site-ip-management", icon: Shield },
          { name: "Queries", href: "/site-queries", icon: MessageCircle },
          { name: "Regulatory", href: "/site-regulatory", icon: FileText },
          { name: "Settings", href: "/site-settings", icon: Settings },
        ],
        referralReview: [
          { name: "Referral Review", href: "/referrals/review", icon: Target },
        ],
        academy: [
          { name: "BRIDGE Academy", href: "/academy", icon: GraduationCap },
        ],
        resources: [
          { name: "Training Videos", href: "/trial-site/training-videos", icon: Video },
          { name: "Webinars", href: "/resources/webinars", icon: Video },
          { name: "Training Manual", href: "/sops", icon: BookOpen },
          { name: "SOPs", href: "/sops", icon: FileText },
          { name: "Integration Hub", href: "/dashboard/integration-hub", icon: Network },
          { name: "DCT Infrastructure", href: "/decentralized-trials", icon: Network },
        ],
      };

    // SMO/CRO navigation - follows WHITE_LABEL_NAVIGATION_STANDARD.md exactly (NO eCRF - SMO/CROs don't do clinical data entry)
    case 'cro':
    case 'smo':
      return {
        smoCroOperations: [
          { name: "SMO/CRO Dashboard", href: "/cro/command-center", icon: BarChart3 },
          { name: "Site CRM", href: "/cro/command-center?tab=crm", icon: Users },
          { name: "Site Network", href: "/cro/command-center?tab=sites", icon: Building2 },
          { name: "Analytics", href: "/cro/command-center?tab=analytics", icon: TrendingUp },
          { name: "Email Sites", href: "/cro/command-center?tab=email", icon: Mail },
          { name: "Staff", href: "/cro/command-center?tab=staff", icon: UserCheck },
          { name: "Settings", href: "/cro/command-center?tab=settings", icon: Settings },
        ],
        referralReview: [
          { name: "Referral Review", href: "/referrals/review", icon: Target },
        ],
        academy: [
          { name: "BRIDGE Academy", href: "/academy", icon: GraduationCap },
        ],
        resources: [
          { name: "CRO One-Pager", href: "/resources/cro-one-pager", icon: FileText },
          { name: "CRA Monitoring One-Pager", href: "/resources/cra-one-pager", icon: Eye },
          { name: "Training Videos", href: "/training-videos", icon: Video },
          { name: "Webinars", href: "/resources/webinars", icon: Video },
          { name: "Training Manual", href: "/training-manual", icon: BookOpen },
          { name: "SOPs", href: "/sops", icon: FileText },
          { name: "DCT Infrastructure", href: "/decentralized-trials", icon: Network },
        ],
      };



      default:
        return {
          main: [
            { name: "Browse Trials", href: "/trials", icon: Search },
            { name: "Directory", href: "/directory", icon: Users },
          ],
          resources: [
            { name: "Education", href: "/education", icon: GraduationCap },
            { name: "Roadmap", href: "/roadmap", icon: TrendingUp },
          ]
        };
    }
  };

  // Get base navigation and apply context filtering
  return filterNavigationByContext(getBaseNavigation());
}

export function formatCategoryName(category: string): string {
  const names: Record<string, string> = {
    myTrials: 'My Trials',
    dashboard: 'Dashboard',
    patients: 'Patients',
    professionalTools: 'Professional Tools',
    trialManagement: 'Trial Management',
    businessTools: 'Business Tools',
    siteOperations: 'Site Operations',
    patientManagement: 'Patient Management',
    smoCroOperations: 'SMO/CRO Operations',
    croOperations: 'CRO Operations',
    resources: 'Resources',
    platform: 'Platform',
    conditions: 'Conditions',
    technology: 'Technology',
    solutions: 'Solutions',
    academy: 'Academy',
    company: 'Company',
    admin: 'Admin',
    main: 'Main',
    regulatory: 'Regulatory',
    operations: 'Operations',
    content: 'Content',
    trialRegistries: 'Trial Registries',
    system: 'System',
    referralReview: 'Referral Review',
    analytics: 'Analytics',
  };
  return names[category] || category;
}
